const INVALID_NUMBER = /(?:NaN|Infinity)/i
const MAX_VIEWPORT_RATIO = 30

function parseViewport(svgNode) {
  const viewBox = (svgNode.getAttribute('viewBox') || '')
    .trim()
    .split(/[\s,]+/)
    .map(Number)

  if (
    viewBox.length === 4
    && viewBox.every(Number.isFinite)
    && viewBox[2] > 0
    && viewBox[3] > 0
  ) {
    return { width: viewBox[2], height: viewBox[3], source: 'viewBox' }
  }

  const width = Number.parseFloat(svgNode.getAttribute('width'))
  const height = Number.parseFloat(svgNode.getAttribute('height'))
  if (Number.isFinite(width) && Number.isFinite(height) && width > 0 && height > 0) {
    return { width, height, source: 'attributes' }
  }

  return null
}

function hasInvalidGeometry(svgNode) {
  const attributes = ['d', 'transform', 'x', 'y', 'x1', 'x2', 'y1', 'y2', 'width', 'height', 'cx', 'cy', 'r']
  return Array.from(svgNode.querySelectorAll('*')).some((node) =>
    attributes.some((attribute) => INVALID_NUMBER.test(node.getAttribute(attribute) || ''))
  )
}

function addAxisAdaptationIssues(issues, axis, result) {
  if (!result) return
  const axisName = axis.toUpperCase()
  if (result.truncateTo != null) {
    issues.push({
      code: `${axis}-labels-shortened`,
      severity: 'review',
      message: `${axisName}-axis labels were shortened to fit.`,
    })
  }
  if (result.rotate || result.stagger) {
    issues.push({
      code: `${axis}-labels-adapted`,
      severity: 'info',
      message: `${axisName}-axis labels were ${result.rotate ? 'rotated' : 'staggered'} to fit.`,
    })
  }
}

function addMarginRecommendationIssues(issues, marginSuggestions) {
  if (!marginSuggestions) return

  const labels = {
    marginLeft: 'left',
    marginRight: 'right',
    marginTop: 'top',
    marginBottom: 'bottom',
  }

  Object.entries(marginSuggestions).forEach(([margin, value]) => {
    if (!labels[margin] || !Number.isFinite(value) || value <= 0) return
    issues.push({
      code: `${margin}-recommended`,
      severity: 'review',
      message: `Increase the ${labels[margin]} chart margin to ${Math.ceil(value)}px to fit its axis labels.`,
    })
  })
}

/**
 * Inspect a finished Lugsail SVG without mutating it. The result is intentionally
 * small and serializable so Canvas can retain it outside chart configuration.
 */
export function getChartLayoutDiagnostics(svgNode, {
  xLabelResult = null,
  yLabelResult = null,
  marginSuggestions = null,
} = {}) {
  if (!svgNode) {
    return {
      status: 'unavailable',
      issues: [{ code: 'svg-unavailable', severity: 'review', message: 'The chart SVG is not available yet.' }],
    }
  }

  const issues = []
  const viewport = parseViewport(svgNode)
  if (!viewport) {
    issues.push({ code: 'invalid-viewport', severity: 'error', message: 'The chart SVG has no usable viewport.' })
  } else {
    const ratio = Math.max(viewport.width / viewport.height, viewport.height / viewport.width)
    if (ratio > MAX_VIEWPORT_RATIO) {
      issues.push({ code: 'disproportionate-viewport', severity: 'review', message: 'The chart SVG viewport is unusually disproportionate.' })
    }
  }

  if (hasInvalidGeometry(svgNode)) {
    issues.push({ code: 'invalid-geometry', severity: 'error', message: 'The chart contains invalid SVG geometry.' })
  }

  addAxisAdaptationIssues(issues, 'x', xLabelResult)
  addAxisAdaptationIssues(issues, 'y', yLabelResult)
  addMarginRecommendationIssues(issues, marginSuggestions)

  const status = issues.some((issue) => issue.severity === 'error')
    ? 'needs-attention'
    : issues.some((issue) => issue.severity === 'review')
      ? 'review'
      : 'good'

  return { status, issues, viewport, marginSuggestions: marginSuggestions || null }
}
